/**
 * Building blocks shared by the pages: button and field styles, page and section headings,
 * labelled facts and figures, status badges, notices, empty and loading states, placeholders,
 * expandable details, a copy button and error messages.
 *
 * Surfaces are white on the light theme and charcoal ink on the dark one, where they get lighter
 * as they come forward. The brand's blue marks what can be acted on, green marks what went well,
 * and headings and key figures are set in the display typeface.
 */

import {
  Check,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  Copy,
  Info,
  type LucideIcon,
  TriangleAlert,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useState } from "react";

import { ApiRequestError } from "../api/client.js";

/** The outline of a focused control, shown when the keyboard moved the focus there. */
export const focusRing =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:focus-visible:outline-brand-400";

/** The page's content column: full width on small screens, at most 80rem wide on large ones. */
const frame = "mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8";

const button =
  "inline-flex items-center justify-center gap-2 rounded-lg whitespace-nowrap transition duration-200 disabled:pointer-events-none disabled:opacity-50";

/**
 * The brand's blue with dark text, which reads well on light and dark surfaces alike. On the dark
 * theme it is a plain blue, lit only along its top edge, without the glow it has on the light
 * theme. Light colours on near-black already stand out, and a glow there looks like neon.
 */
const primary = `${button} bg-gradient-to-b from-brand-300 to-brand-500 font-semibold text-ink-950 shadow-[inset_0_1px_0_rgb(255_255_255/0.45),0_1px_2px_rgb(5_10_12/0.25),0_0_0_1px_rgb(67_139_176/0.55)] hover:from-brand-200 hover:to-brand-400 hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.5),0_6px_24px_-6px_rgb(65_168_198/0.65),0_0_0_1px_rgb(67_139_176/0.6)] active:translate-y-px dark:from-brand-400 dark:to-brand-500 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.3),0_1px_2px_rgb(0_0_0/0.4)] dark:hover:from-brand-300 dark:hover:to-brand-400 dark:hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.35),0_1px_2px_rgb(0_0_0/0.4)] ${focusRing}`;
const secondary = `${button} bg-white font-medium text-zinc-800 shadow-sm ring-1 ring-zinc-900/10 ring-inset hover:bg-zinc-50 hover:ring-zinc-900/20 dark:bg-white/[0.05] dark:text-zinc-100 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04)] dark:ring-white/[0.12] dark:hover:bg-white/[0.08] dark:hover:ring-white/20 ${focusRing}`;

/** Class names of buttons, fields, cards, links and page frames. */
export const styles = {
  primaryButton: `${primary} px-4 py-2 text-sm`,
  secondaryButton: `${secondary} px-3.5 py-2 text-sm`,
  /** A small secondary button, for an action on one row of a list. */
  secondaryButtonSmall: `${secondary} px-2.5 py-1 text-xs`,
  /** The large buttons of the public pages' main actions. */
  primaryButtonLarge: `${primary} px-5 py-3 text-base`,
  secondaryButtonLarge: `${secondary} px-5 py-3 text-base`,
  dangerButton: `${button} bg-white px-3.5 py-2 text-sm font-medium text-red-700 shadow-sm ring-1 ring-red-600/25 ring-inset hover:bg-red-50 dark:bg-red-500/[0.06] dark:text-red-300 dark:ring-red-400/25 dark:hover:bg-red-500/[0.12] ${focusRing}`,
  ghostButton: `${button} px-3 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-900/[0.05] hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-white ${focusRing}`,
  /** A text field: on the dark theme, set a shade into the card it sits on, behind a hairline. */
  input:
    "mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-zinc-400 hover:border-zinc-400 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15 focus:outline-none dark:border-white/[0.1] dark:bg-ink-950/60 dark:text-zinc-100 dark:shadow-none dark:placeholder:text-zinc-500 dark:hover:border-white/20 dark:focus:border-brand-400/70 dark:focus:ring-brand-400/10",
  /** A card: on the dark theme, a step lighter than the page, its top edge catching the light. */
  card: "rounded-2xl bg-white p-5 shadow-[0_1px_2px_rgb(24_24_27/0.04),0_6px_24px_-12px_rgb(24_24_27/0.12)] ring-1 ring-zinc-900/[0.07] sm:p-6 dark:bg-ink-900/80 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04),0_1px_2px_rgb(0_0_0/0.3)] dark:ring-white/[0.07]",
  link: "font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300",
  frame,
  page: `${frame} py-10 sm:py-14`,
  narrowPage: "mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14",
} as const;

/**
 * A label set in a pill with the brand's dot, above a heading. The pill is tinted blue on the
 * light theme and a quiet glass pill on the dark one, where the dot alone carries the colour.
 */
export function Kicker({ children }: { children: ReactNode }) {
  return (
    <p className="inline-flex items-center gap-2 rounded-full bg-brand-500/[0.08] px-2.5 py-1 text-xs font-semibold tracking-wide text-brand-800 ring-1 ring-brand-600/15 ring-inset dark:bg-white/[0.05] dark:text-zinc-200 dark:ring-white/10">
      <span
        aria-hidden
        className="size-1.5 rounded-full bg-gradient-to-br from-brand-400 to-brand-600"
      />
      {children}
    </p>
  );
}

/**
 * A page's eyebrow, title, introduction and actions. It also names the browser tab after the
 * page: "{title} · Morcast", or `documentTitle` when the heading alone would be unclear.
 */
export function PageHeader({
  eyebrow,
  title,
  documentTitle,
  leading,
  actions,
  children,
}: {
  eyebrow?: string;
  title: string;
  documentTitle?: string;
  /** A mark before the title, such as a brand's tile. */
  leading?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
      <title>{`${documentTitle ?? title} · Morcast`}</title>
      <div className="flex min-w-0 items-start gap-4 sm:gap-5">
        {leading && <div className="hidden pt-1 sm:block">{leading}</div>}
        <div className="min-w-0">
          {eyebrow && <Kicker>{eyebrow}</Kicker>}
          <h1
            className={`${eyebrow ? "mt-4" : ""} font-display text-3xl font-semibold tracking-[-0.03em] text-balance text-zinc-950 sm:text-4xl dark:text-white`}
          >
            {title}
          </h1>
          {children && (
            <div className="mt-3 max-w-3xl text-[0.9375rem] leading-7 text-zinc-600 dark:text-zinc-400">
              {children}
            </div>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

/** An icon in a tile of the brand's colours, beside a heading. */
export function IconTile({ icon: Icon, size = "md" }: { icon: LucideIcon; size?: "md" | "lg" }) {
  return (
    <span
      className={`relative grid shrink-0 place-items-center bg-gradient-to-br from-brand-50 to-brand-100 text-brand-700 shadow-[inset_0_1px_0_rgb(255_255_255/0.9)] ring-1 ring-brand-600/15 dark:from-brand-400/[0.12] dark:to-brand-400/[0.04] dark:text-brand-300 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.06)] dark:ring-brand-300/15 ${
        size === "lg" ? "size-12 rounded-2xl" : "size-9 rounded-xl"
      }`}
    >
      <Icon aria-hidden className={size === "lg" ? "size-6" : "size-[1.125rem]"} />
    </span>
  );
}

/**
 * A card with a heading, an optional icon, description and actions, labelled by its heading.
 */
export function Section({
  title,
  icon,
  description,
  actions,
  className = "",
  children,
}: {
  title: string;
  icon?: LucideIcon;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className={`${styles.card} ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {icon && <IconTile icon={icon} />}
          <div className={`min-w-0 ${icon ? "pt-1" : ""}`}>
            <h2
              id={id}
              className="font-display text-lg font-semibold tracking-[-0.015em] text-zinc-950 dark:text-white"
            >
              {title}
            </h2>
            {description && (
              <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{description}</div>
            )}
          </div>
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

/** A labelled value in a description list. */
export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-0.5 font-medium break-words text-zinc-900 dark:text-zinc-100">
        {children}
      </dd>
    </div>
  );
}

/** A key figure in a description list: its label, the value set large, and a short hint. */
export function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string | undefined;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white px-4 py-3.5 shadow-[0_1px_2px_rgb(24_24_27/0.04)] ring-1 ring-zinc-900/[0.07] dark:bg-white/[0.03] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.04)] dark:ring-white/[0.08]">
      <dt className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-1.5 font-display text-xl font-semibold tracking-[-0.02em] break-words text-zinc-950 tabular-nums dark:text-white">
        {value}
      </dd>
      {hint && <dd className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{hint}</dd>}
    </div>
  );
}

const badgeTones = {
  green: {
    classes:
      "bg-emerald-500/[0.08] text-emerald-800 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/25",
    dot: "bg-emerald-500 dark:bg-emerald-400",
  },
  amber: {
    classes:
      "bg-amber-500/10 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/25",
    dot: "bg-amber-500 dark:bg-amber-400",
  },
  red: {
    classes:
      "bg-red-500/[0.08] text-red-800 ring-red-600/20 dark:bg-red-400/10 dark:text-red-300 dark:ring-red-400/25",
    dot: "bg-red-500 dark:bg-red-400",
  },
  grey: {
    classes:
      "bg-zinc-900/[0.05] text-zinc-700 ring-zinc-900/10 dark:bg-white/[0.06] dark:text-zinc-300 dark:ring-white/15",
    dot: "bg-zinc-400 dark:bg-zinc-500",
  },
  blue: {
    classes:
      "bg-sky-500/[0.08] text-sky-800 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/25",
    dot: "bg-sky-500 dark:bg-sky-400",
  },
} as const;

/** The colours a badge comes in. */
export type BadgeTone = keyof typeof badgeTones;

/** A short status label, with a dot of its colour. */
export function Badge({ tone, children }: { tone: BadgeTone; children: string }) {
  const { classes, dot } = badgeTones[tone];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset ${classes}`}
    >
      <span aria-hidden className={`size-1.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

const noticeTones: Record<
  "info" | "success" | "warning" | "error",
  { icon: LucideIcon; classes: string; iconClass: string }
> = {
  info: {
    icon: Info,
    classes:
      "bg-sky-500/[0.06] text-sky-950 ring-sky-600/15 dark:bg-sky-400/[0.07] dark:text-sky-100 dark:ring-sky-400/20",
    iconClass: "text-sky-600 dark:text-sky-300",
  },
  success: {
    icon: CircleCheck,
    classes:
      "bg-emerald-500/[0.06] text-emerald-950 ring-emerald-600/15 dark:bg-emerald-400/[0.07] dark:text-emerald-50 dark:ring-emerald-400/20",
    iconClass: "text-emerald-600 dark:text-emerald-300",
  },
  warning: {
    icon: TriangleAlert,
    classes:
      "bg-amber-500/[0.08] text-amber-950 ring-amber-600/20 dark:bg-amber-400/[0.07] dark:text-amber-50 dark:ring-amber-400/20",
    iconClass: "text-amber-600 dark:text-amber-300",
  },
  error: {
    icon: CircleAlert,
    classes:
      "bg-red-500/[0.06] text-red-950 ring-red-600/15 dark:bg-red-400/[0.07] dark:text-red-50 dark:ring-red-400/20",
    iconClass: "text-red-600 dark:text-red-300",
  },
};

/**
 * A highlighted message: information, a success, a warning or an error. Warnings and errors are
 * announced to screen readers as they appear.
 */
export function Notice({
  tone = "info",
  title,
  className = "",
  children,
}: {
  tone?: keyof typeof noticeTones;
  title?: string;
  className?: string;
  children?: ReactNode;
}) {
  const { icon: Icon, classes, iconClass } = noticeTones[tone];
  return (
    <div
      role={tone === "warning" || tone === "error" ? "alert" : undefined}
      className={`flex gap-3 rounded-xl px-4 py-3 text-sm ring-1 ring-inset ${classes} ${className}`}
    >
      <Icon aria-hidden className={`mt-0.5 size-4 shrink-0 ${iconClass}`} />
      <div className="min-w-0 space-y-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div>{children}</div>}
      </div>
    </div>
  );
}

/** What a list or page shows when there is nothing in it yet, with the step that fills it. */
export function EmptyState({
  icon,
  title,
  action,
  children,
}: {
  icon?: LucideIcon;
  title: string;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-dashed border-zinc-300 bg-white/60 px-6 py-12 text-center dark:border-white/[0.12] dark:bg-white/[0.02]">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 left-1/2 h-48 w-96 -translate-x-1/2 rounded-full bg-brand-400/10 blur-3xl dark:bg-white/[0.04]"
      />
      {icon && (
        <div className="relative flex justify-center">
          <IconTile icon={icon} size="lg" />
        </div>
      )}
      <p className="relative mt-4 font-display font-semibold text-zinc-900 dark:text-white">
        {title}
      </p>
      {children && (
        <div className="relative mx-auto mt-1.5 max-w-md text-sm text-zinc-600 dark:text-zinc-400">
          {children}
        </div>
      )}
      {action && <div className="relative mt-6 flex flex-wrap justify-center gap-3">{action}</div>}
    </div>
  );
}

/**
 * A page that is only a message, such as "Sign in to continue" or "Page not found": an icon, a
 * heading that also names the browser tab, the explanation and what to do next.
 */
export function MessagePage({
  icon: Icon,
  title,
  actions,
  children,
}: {
  icon: LucideIcon;
  title: string;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="relative isolate mx-auto max-w-xl px-4 py-20 text-center sm:py-28">
      <title>{`${title} · Morcast`}</title>
      <div
        aria-hidden
        className="pointer-events-none absolute top-8 left-1/2 -z-10 h-56 w-[28rem] max-w-full -translate-x-1/2 rounded-full bg-gradient-to-br from-brand-400/20 to-brand-600/10 blur-3xl dark:from-white/[0.05] dark:to-brand-400/[0.04]"
      />
      <span className="relative mx-auto grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-300 to-brand-500 text-ink-950 shadow-[inset_0_1px_0_rgb(255_255_255/0.5),0_10px_30px_-10px_rgb(65_168_198/0.7)] dark:from-brand-400 dark:to-brand-500 dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.3),0_10px_30px_-12px_rgb(0_0_0/0.6)]">
        <Icon aria-hidden className="size-6" />
      </span>
      <h1 className="mt-6 font-display text-3xl font-semibold tracking-[-0.03em] text-zinc-950 sm:text-4xl dark:text-white">
        {title}
      </h1>
      {children && (
        <div className="mt-3 leading-7 text-zinc-600 dark:text-zinc-400">{children}</div>
      )}
      {actions && <div className="mt-8 flex flex-wrap justify-center gap-3">{actions}</div>}
    </section>
  );
}

/** A spinner with a label, announced to screen readers, while data loads. */
export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <p role="status" className="flex items-center gap-2.5 text-sm text-zinc-500 dark:text-zinc-400">
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-brand-500/20 border-t-brand-500 dark:border-brand-400/20 dark:border-t-brand-400"
      />
      {label}
    </p>
  );
}

/** A placeholder in the shape of content that is loading, with a moving sheen. */
export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div aria-hidden className={`skeleton rounded-lg motion-safe:animate-shimmer ${className}`} />
  );
}

/** Details most people can skip, such as on-chain parameters, behind a summary line. */
export function Disclosure({
  summary,
  defaultOpen = false,
  children,
}: {
  summary: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details
      open={defaultOpen}
      className="group rounded-xl bg-white/50 ring-1 ring-zinc-900/[0.08] dark:bg-white/[0.02] dark:ring-white/10 [&_summary::-webkit-details-marker]:hidden"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm font-medium transition-colors hover:bg-zinc-900/[0.03] dark:hover:bg-white/[0.04]">
        {summary}
        <ChevronDown
          aria-hidden
          className="size-4 shrink-0 text-zinc-500 transition-transform duration-200 group-open:rotate-180 dark:text-zinc-400"
        />
      </summary>
      <div className="border-t border-zinc-900/[0.08] px-4 py-4 text-sm dark:border-white/10">
        {children}
      </div>
    </details>
  );
}

/** Copies a value, such as an address, to the clipboard and confirms it briefly. */
export function CopyButton({
  value,
  label = "Copy",
  compact = false,
}: {
  value: string;
  label?: string;
  /** Only the icon, for a value in a line of text or a table. The label names it to screen readers. */
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => setCopied(true));
      }}
      {...(compact ? { "aria-label": copied ? "Copied" : label, title: label } : {})}
      className={`inline-flex shrink-0 items-center gap-1 rounded-md text-xs font-medium text-zinc-500 transition-colors hover:bg-zinc-900/[0.05] hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-zinc-100 ${compact ? "p-1 align-middle" : "px-1.5 py-1"} ${focusRing}`}
    >
      {copied ? (
        <Check aria-hidden className="size-3.5 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <Copy aria-hidden className="size-3.5" />
      )}
      {!compact && (copied ? "Copied" : label)}
    </button>
  );
}

/** An error with its message and, when the API lists them, each problem. */
export function ErrorMessage({ error }: { error: Error }) {
  const problems = error instanceof ApiRequestError ? error.problems : [];
  return <Problems message={error.message} problems={problems} />;
}

/** A message with an optional list of problems, announced to screen readers. */
export function Problems({ message, problems }: { message: string; problems: string[] }) {
  return (
    <Notice tone="error" className="mt-4">
      <p>{message}</p>
      {problems.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
    </Notice>
  );
}
